import { Suspense, useRef } from 'react';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { QueryClient, QueryClientProvider } from 'react-query';

import { Flex, Spinner } from '@chakra-ui/react';

import { WithIdentifyAuthedUser } from '~/features/analytics/components/with-identify-authed-user';
import { useCheckUserCookies } from '~/features/cookies/hooks/use-check-user-cookies';
import { WithInternationalization } from '~/features/internationalization/with-internationalization';
import { useWelcomeTrap } from '~/features/welcome-flow/hooks/use-welcome-trap';
import { useCancelClientNavigation } from '~/hooks/use-cancel-client-navigation';
import { useDeploymentVersionCheck } from '~/hooks/use-deployment-version-check';
import { initDatadog } from '~/libs/datadog';
import { WithMocks } from '~/mocks/with-mocks';
import { UDS2ThemeProvider } from '~/styles/theme/uds-v2-theme-provider';

const WelcomeModal = dynamic<{}>(
  () => import('~/features/welcome-flow/components/welcome-modal').then((mod) => mod.WelcomeModal),
  { ssr: false }
);
const PDPopupMount = dynamic<{}>(
  () => import('~/features/personalized-discount-popup/components/pd-popup-mount').then((mod) => mod.PDPopupMount),
  { ssr: false }
);
const ExperimentSettingsButton = dynamic<{}>(() =>
  import('~/features/experiments/components/experiment-settings-button').then((mod) => mod.ExperimentSettingsButton)
);

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    qualified: any; // Note: This is a global function that is set by the qualified script. Unfortunately, there are no type modules for this.
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      refetchOnMount: true,
      staleTime: Infinity,
      cacheTime: 60 * 1000,
    },
  },
});

export default function App({ Component, pageProps }: AppProps<{ themeProvider?: 'uds2' }>) {
  return (
    <WithMocks>
      <WithBootstrap>
        <Head>
          <meta content='width=device-width, initial-scale=1' name='viewport' />
        </Head>
        <QueryClientProvider client={queryClient}>
          <CookieReconciler />
          <UDS2ThemeProvider>
            <Suspense
              fallback={
                <Flex alignItems='center' inset={0} justifyContent='center' position='fixed'>
                  <Spinner size='xl' />
                </Flex>
              }
            >
              <WithInternationalization>
                <WithIdentifyAuthedUser>
                  <WelcomeModal />
                  <PDPopupMount />
                  <Component {...pageProps} />
                  {process.env.NEXT_PUBLIC_ENVIRONMENT_NAME !== 'production' && <ExperimentSettingsButton />}
                </WithIdentifyAuthedUser>
              </WithInternationalization>
            </Suspense>
          </UDS2ThemeProvider>
        </QueryClientProvider>
      </WithBootstrap>
    </WithMocks>
  );
}

function WithBootstrap({ children }: { children: React.ReactNode }) {
  const datadogLoaded = useRef(false);

  if (!datadogLoaded.current && typeof window !== 'undefined') {
    initDatadog();
    datadogLoaded.current = true;
  }

  useCancelClientNavigation();
  useDeploymentVersionCheck();
  useWelcomeTrap();

  return <>{children}</>;
}

function CookieReconciler() {
  useCheckUserCookies();
  return null;
}
